(() => {
  const configNode = document.querySelector('[data-diarist-consent-config]');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const digitalIds = new Set(config.productIds.map(String));
  const consentKeys = ['Digital consent', 'Digital consent version', 'Digital consent items', 'Digital consent accepted at'];
  let revision = 0;
  let pending = 0;
  let quantityDirty = false;
  let checkingOut = false;
  let clearPromise = Promise.resolve();
  const allowed = new WeakSet();
  const initializedProducts = new WeakSet();
  const cartBoxes = () => [...document.querySelectorAll('[data-diarist-consent="cart"]')];
  const productBoxes = () => [...document.querySelectorAll('[data-diarist-consent="product"]')];
  const checkbox = box => box?.querySelector('[data-diarist-consent-checkbox]');
  const fingerprint = items => JSON.stringify(items.map(item => [item.key, item.quantity]).sort((a,b) => a[0].localeCompare(b[0])));
  const digitalItems = cart => cart.items.filter(item => digitalIds.has(String(item.product_id)));
  function showError(box, message) {
    if (!box) return;
    const error = box.querySelector('[role="alert"]');
    error.textContent = message;
    error.hidden = false;
    checkbox(box).focus();
  }
  function reset(box) {
    const input = checkbox(box);
    input.checked = false;
    input.setCustomValidity('');
    box.querySelector('[data-diarist-consent-time]').value = '';
    box.querySelector('[role="alert"]').hidden = true;
    if (box.dataset.diaristConsent === 'product') syncExpress(box);
  }
  function syncExpress(box) {
    const form = box.closest('form');
    const payment = form?.querySelector('[data-diarist-express-payment]');
    if (!form || !payment) return;
    const terms = form.querySelector('terms-and-condition-checkbox input[type="checkbox"]');
    const variant = form.querySelector('[name="id"]');
    const add = form.querySelector('[name="add"]');
    const ready = checkbox(box).checked && (!terms || terms.checked) &&
      Boolean(variant?.value) && !add?.disabled && pending === 0 && productFormValid(form);
    payment.toggleAttribute('inert', !ready);
    payment.inert = !ready;
    payment.setAttribute('aria-disabled', String(!ready));
    const time = box.querySelector('[data-diarist-consent-time]');
    if (ready && !time.value) time.value = new Date().toISOString();
    if (!ready) time.value = '';
    box.querySelectorAll('[data-diarist-consent-record]').forEach(input => { input.disabled = !ready; });
  }
  function initializeProducts() {
    productBoxes().forEach(box => {
      if (initializedProducts.has(box)) return;
      initializedProducts.add(box);
      reset(box);
    });
  }
  function invalidate() {
    revision++;
    cartBoxes().forEach(reset);
  }
  async function clearStoredConsent() {
    const response = await fetch(config.updateUrl, {
      method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify({attributes: Object.fromEntries(consentKeys.map(key => [key, '']))})
    });
    if (!response.ok) throw new Error('consent-update');
    return response.json();
  }
  function productFormValid(form) {
    return form.dataset.variantPending !== 'true' && form.dataset.variantInvalid !== 'true';
  }
  window.DiaristDigitalConsent = {
    productFormValid,
    beginCartChange() { pending++; invalidate(); productBoxes().forEach(syncExpress); },
    endCartChange() { pending = Math.max(0, pending - 1); quantityDirty = false; productBoxes().forEach(syncExpress); },
    invalidate
  };
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-diarist-consent-checkbox]')) {
      const box = input.closest('[data-diarist-consent]');
      input.setCustomValidity('');
      box.querySelector('[data-diarist-consent-time]').value = input.checked ? new Date().toISOString() : '';
      box.querySelector('[role="alert"]').hidden = true;
      if (box.dataset.diaristConsent === 'product') syncExpress(box);
    } else if (input.matches('[name="updates[]"]')) {
      quantityDirty = true;
      invalidate();
    } else if ((input.name === 'id' || input.name === 'quantity') && input.closest('product-form')) {
      const box = input.closest('product-form').querySelector('[data-diarist-consent="product"]');
      if (box) reset(box);
    } else if (input.closest('terms-and-condition-checkbox')) {
      const box = input.closest('form')?.querySelector('[data-diarist-consent="product"]');
      if (box) syncExpress(box);
    }
  }, true);
  document.addEventListener('input', event => {
    if (event.target.name !== 'quantity') return;
    const box = event.target.closest('product-form')?.querySelector('[data-diarist-consent="product"]');
    if (box) reset(box);
  }, true);
  document.addEventListener('diarist:variant-state', event => {
    const section = event.target.closest('.shopify-section, .popup-modal__content__data') || event.target;
    section.querySelectorAll('[data-diarist-consent="product"]').forEach(event.detail.pending ? reset : syncExpress);
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-type="add-to-cart-form"]')) {
      if (!productFormValid(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      return;
    }
    if (!['cart', 'CartDrawer-Form'].includes(form.id)) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    if (allowed.has(form)) {
      allowed.delete(form);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const box = cartBoxes().find(item => item.dataset.formId === form.id);
    if (pending || quantityDirty) {
      if (box && !box.hidden) showError(box, config.waitMessage);
      return;
    }
    if (box && !box.hidden && !checkbox(box).checked) {
      showError(box, box.dataset.requiredMessage);
      checkbox(box).reportValidity();
      return;
    }
    if (checkingOut) return;
    checkingOut = true;
    const startRevision = revision;
    const submitter = event.submitter || document.querySelector(`[name="checkout"][form="${form.id}"]`);
    if (submitter) submitter.setAttribute('data-diarist-checkout-busy', '');
    (async () => {
      await clearPromise;
      const response = await fetch(config.cartUrl + '.js', {headers: {'Accept': 'application/json'}, cache: 'no-store'});
      if (!response.ok) throw new Error('cart-read');
      const cart = await response.json();
      if (revision !== startRevision || pending || quantityDirty) throw new Error('cart-changing');
      const actual = digitalItems(cart);
      const rendered = box ? JSON.parse(box.querySelector('[data-diarist-digital-items]').textContent) : [];
      if (fingerprint(actual) !== fingerprint(rendered)) {
        invalidate();
        window.location.assign(config.cartUrl);
        return;
      }
      if (actual.length && (!box || box.hidden || !checkbox(box).checked)) throw new Error('consent-required');
      if (consentKeys.some(key => cart.attributes?.[key])) await clearStoredConsent();
      if (revision !== startRevision || pending || quantityDirty) throw new Error('cart-changing');
      if (actual.length) box.querySelector('[data-diarist-consent-time]').value = new Date().toISOString();
      if (!form.reportValidity()) return;
      allowed.add(form);
      if (submitter) form.requestSubmit(submitter); else form.requestSubmit();
    })().catch(() => {
      if (box && !box.hidden) showError(box, config.retryMessage);
      else {
        const error = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (error) { error.textContent = config.retryMessage; error.setAttribute('role', 'alert'); }
      }
    }).finally(() => {
      checkingOut = false;
      submitter?.removeAttribute('data-diarist-checkout-busy');
    });
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const target = new URL(link.href, window.location.origin);
    if (target.origin === window.location.origin && /\/checkout(?:\/|$)/.test(target.pathname)) {
      event.preventDefault();
      window.location.assign(config.cartUrl);
    }
  }, true);
  function freshPage() {
    invalidate();
    initializeProducts();
    productBoxes().forEach(reset);
    if (config.hasStoredConsent) clearPromise = clearStoredConsent().catch(() => {});
  }
  freshPage();
  new MutationObserver(mutations => {
    const addedConsent = mutations.some(mutation => [...mutation.addedNodes].some(node =>
      node.nodeType === 1 && (node.matches('[data-diarist-consent="product"]') || node.querySelector('[data-diarist-consent="product"]'))
    ));
    if (addedConsent) initializeProducts();
  }).observe(document.documentElement, {childList: true, subtree: true});
  window.addEventListener('pageshow', event => { if (event.persisted) freshPage(); });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, invalidate);
  }
})();
